import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faMagnifyingGlass, faFloppyDisk, faRotateLeft, faTableList,
  faTriangleExclamation, faPenToSquare, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../../components/SearchableSelect';

/** Beyond this, a cell is shown truncated and edited in a textarea rather than an input. */
const LONG_TEXT = 60;

/**
 * The project's "Détails" sheet: one row per imported line, arbitrary columns.
 *
 * Staff-only — prestataires never see this (the whole route group excludes them). Every
 * imported cell is editable in place, and the prestataire of a row can be bound, moved or
 * cleared here, which is how a sheet imported without « Ref » gets assigned afterwards.
 */
const DetailsView = ({ quotaProjectId, refreshKey = 0, onLoaded }) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [overallTarget, setOverallTarget] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  // Cell being edited: { rowId, key, value }. Only one at a time — a details sheet is
  // read far more often than it is corrected.
  const [editing, setEditing] = useState(null);
  const [savingCell, setSavingCell] = useState(false);

  // Draft prestataire per row; nothing is sent until the row's save button is pressed.
  const [draftAssign, setDraftAssign] = useState({});
  const [assigningId, setAssigningId] = useState(null);

  // Held in a ref so an inline arrow from the parent doesn't re-trigger the fetch.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/quotas/projects/${quotaProjectId}/details`);
      const d = res.data?.data || {};
      setColumns(d.columns || []);
      setRows(d.rows || []);
      setAssignables(d.assignables || []);
      setCanManage(!!d.can_manage);
      setOverallTarget(d.overall_target || 0);
      setError('');
      if (onLoadedRef.current) onLoadedRef.current((d.rows || []).length);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  }, [quotaProjectId]);

  useEffect(() => { fetchRows(); }, [fetchRows, refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyUnassigned && r.supervisor) return false;
      if (!q) return true;
      const hay = [
        r.ref || '', r.supervisor?.name || '', r.quota_cell?.cell_code || '',
        ...Object.values(r.data || {}),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, onlyUnassigned]);

  const unassignedCount = useMemo(() => rows.filter((r) => !r.supervisor).length, [rows]);

  const handleSaveCell = async () => {
    if (!editing) return;
    const { rowId, key, value } = editing;
    const row = rows.find((r) => r.id === rowId);
    if (!row || (row.data?.[key] ?? '') === value) { setEditing(null); return; }

    setSavingCell(true);
    setError('');
    try {
      const res = await axios.patch(`/quotas/details/${rowId}`, { data: { [key]: value } });
      const updated = res.data?.data;
      setRows((prev) => prev.map((r) => r.id === rowId
        ? { ...r, data: updated?.data ?? { ...r.data, [key]: value }, quota_cell: updated?.quota_cell ?? r.quota_cell }
        : r));
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la cellule');
    } finally {
      setSavingCell(false);
    }
  };

  /**
   * Bind / move / clear a row's prestataire. The server re-checks that the prestataire is
   * on the project and still has room, so a rejection is surfaced as-is and the row is
   * left untouched.
   */
  const handleAssign = async (row) => {
    const supervisorId = draftAssign[row.id] === undefined ? (row.supervisor?.id || null) : draftAssign[row.id];
    if ((row.supervisor?.id || null) === supervisorId) return;

    setAssigningId(row.id);
    setError('');
    try {
      const res = await axios.patch(`/quotas/details/${row.id}/supervisor`, { supervisor_id: supervisorId });
      const updated = res.data?.data;
      setRows((prev) => prev.map((r) => r.id === row.id
        ? { ...r, ref: updated?.ref ?? null, supervisor: updated?.supervisor ?? null }
        : r));
      if (res.data?.assignables) setAssignables(res.data.assignables);
      setDraftAssign((prev) => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'assignation');
    } finally {
      setAssigningId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-gray-500">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
        Chargement des détails...
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="py-16 text-center">
        <FontAwesomeIcon icon={faTableList} className="text-4xl text-gray-300 mb-3" />
        <p className="text-gray-600 font-medium">Aucun détail importé pour ce projet</p>
        <p className="text-sm text-gray-500 mt-1">
          Utilisez le menu <strong>Détails</strong> ci-dessus pour télécharger le modèle et importer un fichier Excel.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans les détails..."
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md w-72"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} />
          Non assignées {unassignedCount > 0 && <span className="text-amber-700">({unassignedCount})</span>}
        </label>

        <div className="ml-auto text-sm text-gray-500">
          {filtered.length} / {rows.length} ligne(s)
          {overallTarget > 0 && <> · objectif du quota : {overallTarget}</>}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      <div className="overflow-auto max-h-[65vh] border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-left text-gray-600">
              <th className="py-2 px-3 font-medium w-12">#</th>
              <th className="py-2 px-3 font-medium min-w-[230px]">Prestataire</th>
              <th className="py-2 px-3 font-medium whitespace-nowrap">
                Cellule
                <span className="ml-1 text-[10px] font-normal text-gray-400 uppercase tracking-wide">indicatif</span>
              </th>
              {columns.map((c) => (
                <th key={c.key} className="py-2 px-3 font-medium whitespace-nowrap">
                  {c.label}
                  {c.dimension_id && (
                    <span className="ml-1 text-[10px] font-normal text-indigo-500 uppercase tracking-wide">dimension</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={columns.length + 3} className="py-8 text-center text-gray-500">
                  Aucune ligne ne correspond à la recherche
                </td>
              </tr>
            )}

            {filtered.map((r) => {
              const draft = draftAssign[r.id];
              const currentId = r.supervisor?.id || null;
              const value = draft === undefined ? currentId : draft;
              const dirty = value !== currentId;
              const chosen = assignables.find((s) => s.id === value);

              return (
                <tr key={r.id} className="hover:bg-gray-50 align-top">
                  <td className="py-2 px-3 text-gray-400">{r.row_index}</td>

                  <td className="py-2 px-3">
                    {canManage ? (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-[210px] rounded-md ${dirty ? 'ring-2 ring-indigo-400' : ''}`}>
                            <SearchableSelect
                              options={assignables}
                              selectedValue={value}
                              onChange={(id) => setDraftAssign((prev) => ({ ...prev, [r.id]: id ?? null }))}
                              disabled={assigningId === r.id}
                              placeholder="Non assigné"
                              searchPlaceholder="Nom ou réf..."
                              maxHeight="200px"
                              triggerClassName="px-2 py-1"
                              textClassName="text-xs"
                              showTriggerMeta={false}
                              getOptionLabel={(s) => s.name}
                              getOptionMeta={(s) => `${s.ref || '—'} · ${s.used}/${s.assigned}`
                                + (s.id !== currentId && s.remaining <= 0 ? ' · complet' : '')}
                            />
                          </div>

                          {dirty && (
                            <button
                              type="button"
                              onClick={() => handleAssign(r)}
                              disabled={assigningId === r.id}
                              title="Enregistrer l'assignation"
                              className="p-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              <FontAwesomeIcon
                                icon={assigningId === r.id ? faSpinner : faFloppyDisk}
                                className={assigningId === r.id ? 'animate-spin' : ''}
                              />
                            </button>
                          )}
                          {dirty && assigningId !== r.id && (
                            <button
                              type="button"
                              onClick={() => setDraftAssign((prev) => { const n = { ...prev }; delete n[r.id]; return n; })}
                              title="Annuler la modification"
                              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600"
                            >
                              <FontAwesomeIcon icon={faRotateLeft} />
                            </button>
                          )}
                        </div>

                        <div className="text-xs text-gray-500 mt-0.5">
                          {chosen
                            ? <>({chosen.ref || '—'}) · {chosen.used}/{chosen.assigned} ligne(s)</>
                            : <span className="text-amber-700">Non assignée</span>}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-gray-900">
                          {r.supervisor?.name || <span className="text-amber-700">Non assignée</span>}
                        </div>
                        {r.supervisor?.ref && <div className="text-xs text-gray-500">({r.supervisor.ref})</div>}
                      </div>
                    )}
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    {r.quota_cell
                      ? <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-medium">{r.quota_cell.cell_code}</span>
                      : <span className="text-gray-400 text-xs">—</span>}
                  </td>

                  {columns.map((c) => {
                    const cellValue = r.data?.[c.key] ?? '';
                    const isEditing = editing && editing.rowId === r.id && editing.key === c.key;
                    const isLong = String(cellValue).length > LONG_TEXT;

                    if (isEditing) {
                      return (
                        <td key={c.key} className="py-2 px-3 min-w-[220px]">
                          {isLong || String(editing.value).length > LONG_TEXT ? (
                            <textarea
                              autoFocus
                              rows={4}
                              value={editing.value}
                              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                              onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
                              className="w-full text-sm border border-indigo-300 rounded px-2 py-1"
                            />
                          ) : (
                            <input
                              autoFocus
                              type="text"
                              value={editing.value}
                              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveCell();
                                if (e.key === 'Escape') setEditing(null);
                              }}
                              className="w-full text-sm border border-indigo-300 rounded px-2 py-1"
                            />
                          )}
                          <div className="flex items-center gap-1.5 mt-1">
                            <button
                              type="button"
                              onClick={handleSaveCell}
                              disabled={savingCell}
                              className="px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              <FontAwesomeIcon icon={savingCell ? faSpinner : faFloppyDisk} className={savingCell ? 'animate-spin mr-1' : 'mr-1'} />
                              Enregistrer
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing(null)}
                              className="px-2 py-1 text-xs rounded text-gray-600 hover:bg-gray-100"
                            >
                              Annuler
                            </button>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={c.key}
                        className={`py-2 px-3 text-gray-700 ${canManage ? 'cursor-text group' : ''}`}
                        onClick={() => canManage && setEditing({ rowId: r.id, key: c.key, value: String(cellValue) })}
                        title={canManage ? 'Cliquer pour modifier' : undefined}
                      >
                        <div className="flex items-start gap-1.5">
                          <span className={isLong ? 'block max-w-[320px] truncate' : ''}>
                            {cellValue !== '' ? cellValue : <span className="text-gray-300">—</span>}
                          </span>
                          {canManage && (
                            <FontAwesomeIcon
                              icon={faPenToSquare}
                              className="mt-0.5 text-gray-300 opacity-0 group-hover:opacity-100 text-xs"
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DetailsView;
